import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Users, Check, X, Loader2, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  cn,
} from "@tabipla/ui";
import { useGroups, useAssignTripToGroup } from "@/application/hooks/useGroups";
import type { GroupResponseDto } from "@tabipla/shared";

interface GroupSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId: string;
  currentGroupId: string | null;
  onSuccess?: () => void;
}

export function GroupSelectDialog({
  open,
  onOpenChange,
  tripId,
  currentGroupId,
  onSuccess,
}: GroupSelectDialogProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const { data: groups = [], isLoading } = useGroups();
  const assignMutation = useAssignTripToGroup(tripId);

  // Filter groups that user can assign trips to (editor or owner role)
  const editableGroups = groups.filter((g) => g.myRole === "owner" || g.myRole === "editor");

  // Filter by search query
  const filteredGroups = editableGroups.filter((g) =>
    g.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectGroup = async (groupId: string | null) => {
    try {
      await assignMutation.mutateAsync(groupId);
      if (groupId) {
        toast.success(t("trips.groupUpdated", "グループを更新しました"));
      } else {
        toast.success(t("trips.groupRemoved", "グループから外しました"));
      }
      onSuccess?.();
      onOpenChange(false);
    } catch {
      // Error is handled by mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t("trips.selectGroup.title", "グループを選択")}
          </DialogTitle>
          <DialogDescription>
            {t("trips.selectGroup.description", "この旅行を紐づけるグループを選んでください")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          {editableGroups.length > 5 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("trips.selectGroup.searchPlaceholder", "グループを検索...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          )}

          {/* Group List */}
          <div className="max-h-[300px] space-y-2 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : editableGroups.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {t("trips.selectGroup.noGroups", "編集可能なグループがありません")}
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {t("trips.selectGroup.noResults", "検索結果がありません")}
              </div>
            ) : (
              filteredGroups.map((group) => (
                <GroupItem
                  key={group.id}
                  group={group}
                  isSelected={group.id === currentGroupId}
                  isLoading={assignMutation.isPending}
                  onSelect={() => handleSelectGroup(group.id)}
                />
              ))
            )}
          </div>

          {/* Unlink option */}
          {currentGroupId && (
            <Button
              variant="outline"
              className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => handleSelectGroup(null)}
              disabled={assignMutation.isPending}
            >
              {assignMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <X className="mr-2 h-4 w-4" />
              )}
              {t("trips.selectGroup.unlink", "グループから外す")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface GroupItemProps {
  group: GroupResponseDto;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: () => void;
}

function GroupItem({ group, isSelected, isLoading, onSelect }: GroupItemProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isLoading || isSelected}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-all",
        isSelected
          ? "border-primary/50 bg-primary/5"
          : "hover:border-primary/30 hover:bg-accent/50",
        isLoading && "opacity-50"
      )}
    >
      {/* Group Icon */}
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg",
          group.iconColor ? `bg-gradient-to-br ${group.iconColor}` : "bg-primary/10"
        )}
      >
        {group.iconEmoji || <Users className="h-5 w-5 text-primary" />}
      </div>

      {/* Group Info */}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{group.name}</p>
        <p className="text-sm text-muted-foreground">
          {group.memberCount} {t("groups.members", "メンバー")}
          {group.myRole === "owner" && (
            <span className="ml-2 text-xs text-primary">{t("groups.roles.owner", "オーナー")}</span>
          )}
        </p>
      </div>

      {/* Selected Indicator */}
      {isSelected && (
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-4 w-4" />
        </div>
      )}
    </button>
  );
}
